import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Checkbox, Flag, PlatformIcon, TagChip } from '@/components/ui';
import { ServerLink, SiteDomain, SiteState, TierTag, type SiteSelection } from '@/components/sites/SitesTable';
import { sitePlatform } from '@/utils/labels';
import type { SiteOverview } from '@/types/api';

/** How many tags a card shows before it counts the rest. */
const TAGS_SHOWN = 3;

/**
 * The site list as cards, each led by the picture of the site's front page
 * (ADR-0034) — for recognising a site by its look rather than its name.
 */
export function SitesGrid({ sites, selection }: { readonly sites: readonly SiteOverview[]; readonly selection?: SiteSelection }): ReactNode {
  return (
    <div className={selection && selection.chosen.size > 0 ? 'card-grid card-grid-selecting' : 'card-grid'}>
      {sites.map((site) => {
        const chosen = selection?.chosen.has(site.id) ?? false;
        const platform = sitePlatform(site);
        return (
          <article key={site.id} className={`picture-card${chosen ? ' picture-card-selected' : ''}`}>
            <div className="picture-card-shot">
              {site.previewTakenAt ? (
                <img src={`/api/v1/sites/${site.id}/preview?t=${encodeURIComponent(site.previewTakenAt)}`} alt="" loading="lazy" decoding="async" />
              ) : (
                <div className="picture-card-placeholder">
                  <PlatformIcon platform={platform.key} size={32} />
                  <span>Снимка ещё нет</span>
                </div>
              )}
              {selection ? (
                <div className="picture-card-select">
                  <Checkbox
                    checked={chosen}
                    onChange={(on) => selection.toggle([site.id], on)}
                    label={<span className="visually-hidden">Выбрать {site.displayName}</span>}
                  />
                </div>
              ) : null}
            </div>

            <div className="picture-card-body">
              <div className="picture-card-title">
                <span title={platform.label} className="row-tight">
                  <PlatformIcon platform={platform.key} />
                </span>
                <Flag country={site.country} language={site.language} />
                <Link to={`/sites/${site.id}`} className="picture-card-link" title={site.displayName}>
                  {site.displayName}
                </Link>
                <TierTag tier={site.tier} />
              </div>
              <div className="picture-card-raised row-tight">
                <SiteDomain domain={site.primaryDomain} />
              </div>
              {site.tags.length > 0 ? (
                <div className="row-tight row-wrap">
                  {site.tags.slice(0, TAGS_SHOWN).map((tag) => (
                    <TagChip key={tag.id} tag={tag} size="sm" />
                  ))}
                  {site.tags.length > TAGS_SHOWN ? <span className="muted text-sm">+{site.tags.length - TAGS_SHOWN}</span> : null}
                </div>
              ) : null}
              <div className="picture-card-foot">
                <SiteState site={site} />
                <ServerLink site={site} empty="" />
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
